import { Controller } from "@hotwired/stimulus"

// Thread — drives the in-app messaging thread UI (operator + portal share it):
//   - scroll-to-bottom on open (newest message is at the bottom, LOCKED)
//   - "N new / jump to latest" pill when a message appends while scrolled up
//     (never force-scrolls a reader who has scrolled up)
//   - reverse infinite scroll: an IntersectionObserver on the top sentinel
//     fetches the next (older) Pagy page and PREPENDS it, compensating scrollTop
//     so the viewport stays put.
//
// Targets: list (the scroll container), sentinel (top "load older" marker),
// form (the compose form), jumpButton (the "new messages" pill).
//
// Backfill-on-wake (mobile): a phone/PWA suspends its WebSocket when backgrounded,
// so a message broadcast while the socket is asleep/reconnecting is MISSED — Turbo
// Streams don't backfill on reconnect. When the thread becomes visible/online/
// focused again, refresh() re-fetches the current conversation's server-rendered
// markup and swaps ONLY the messages container's innerHTML, so nothing appended
// while asleep is lost. It never touches the compose form (in-progress typing
// survives) and never replaces the whole frame. The live-append (Turbo Stream)
// path is unchanged; this only covers the missed-while-asleep gap.
export default class extends Controller {
  static targets = ["list", "sentinel", "form", "jumpButton"]
  static values = { url: String }

  connect() {
    this.newCount = 0
    this.loadingOlder = false
    this.pendingBottomMessages = 0
    this.refreshing = false
    // Was the reader pinned to the bottom BEFORE the latest append? Tracked on
    // scroll, because checking atBottom() AFTER a message appends is too late —
    // the new content already grew the scroll height, so it would read "not at
    // bottom" and the message would never auto-scroll into view.
    this.pinnedToBottom = true

    this.scrollToBottom()

    // Track whether the reader is pinned to the bottom.
    this.onScroll = this.handleScroll.bind(this)
    this.listTarget.addEventListener("scroll", this.onScroll)

    // React to appended messages (the Turbo Stream broadcast mutates the list).
    this.observer = new MutationObserver(this.handleMutations.bind(this))
    this.observer.observe(this.listTarget, { childList: true, subtree: true })

    // Clear the pill once a new send goes out from this tab.
    if (this.hasFormTarget) {
      this.onSubmit = this.resetOnSubmit.bind(this)
      this.formTarget.addEventListener("submit", this.onSubmit)
    }

    this.setupSentinel()
    this.setupWakeBackfill()
  }

  disconnect() {
    this.listTarget.removeEventListener("scroll", this.onScroll)
    if (this.observer) this.observer.disconnect()
    if (this.sentinelObserver) this.sentinelObserver.disconnect()
    if (this.hasFormTarget && this.onSubmit) {
      this.formTarget.removeEventListener("submit", this.onSubmit)
    }
    this.teardownWakeBackfill()
  }

  // --- scrolling ------------------------------------------------------------

  scrollToBottom() {
    this.listTarget.scrollTop = this.listTarget.scrollHeight
    this.pinnedToBottom = true
  }

  atBottom() {
    const el = this.listTarget
    // 40px slack so "basically at the bottom" still counts.
    return el.scrollHeight - el.scrollTop - el.clientHeight < 40
  }

  handleScroll() {
    this.pinnedToBottom = this.atBottom()
    if (this.pinnedToBottom) this.hideJump()
  }

  // A message (or older page) was inserted. Distinguish the two insert paths:
  //   - APPEND at the bottom (Message#broadcasts_to → a new message)
  //   - PREPEND at the top (loadOlder → an older page)
  // Only bottom appends drive the jump pill / auto-scroll. If a broadcast lands
  // WHILE we're prepending an older page (loadingOlder), the mutation would be
  // ambiguous, so we defer it: record that a bottom message arrived and reconcile
  // once loadOlder finishes (see reconcileAfterLoadOlder).
  handleMutations(mutations) {
    const appendedAtBottom = mutations.some((m) => this.mutationAddedBottomMessage(m))
    if (!appendedAtBottom) return

    if (this.loadingOlder) {
      // A broadcast arrived mid-prepend; remember it and handle it after.
      this.pendingBottomMessages = (this.pendingBottomMessages || 0) + 1
      return
    }

    this.registerNewBottomMessage()
  }

  // Did this mutation add a message node as the LAST child of the messages
  // container (a bottom append), as opposed to an older-page prepend at the top?
  mutationAddedBottomMessage(mutation) {
    const container = this.listTarget.querySelector("[id$='_messages']")
    if (!container) return mutation.addedNodes.length > 0

    return Array.from(mutation.addedNodes).some(
      (node) =>
        node.nodeType === Node.ELEMENT_NODE &&
        node.parentNode === container &&
        node === container.lastElementChild
    )
  }

  // A new bottom message: pin the reader if they're already at the bottom,
  // otherwise increment + reveal the "N new" jump pill.
  registerNewBottomMessage() {
    // Was-at-bottom (BEFORE this append) → follow the new message down; otherwise
    // the reader has scrolled up, so count it and show the "N new" pill instead.
    if (this.pinnedToBottom) {
      requestAnimationFrame(() => this.scrollToBottom())
    } else {
      this.newCount += 1
      this.showJump()
    }
  }

  // After an older-page prepend completes, replay any bottom messages that
  // arrived mid-load so the jump pill isn't missed (the broadcast race).
  reconcileAfterLoadOlder() {
    const pending = this.pendingBottomMessages || 0
    this.pendingBottomMessages = 0
    if (pending === 0) return

    if (this.pinnedToBottom) {
      requestAnimationFrame(() => this.scrollToBottom())
    } else {
      this.newCount += pending
      this.showJump()
    }
  }

  showJump() {
    if (!this.hasJumpButtonTarget) return
    this.jumpButtonTarget.textContent = `${this.newCount} new message${this.newCount === 1 ? "" : "s"} ↓`
    this.jumpButtonTarget.classList.remove("hidden")
  }

  hideJump() {
    if (!this.hasJumpButtonTarget) return
    this.newCount = 0
    this.jumpButtonTarget.classList.add("hidden")
  }

  jump() {
    this.scrollToBottom()
    this.hideJump()
  }

  resetOnSubmit() {
    // The sender wants to see their own message; pin them to the bottom.
    requestAnimationFrame(() => this.scrollToBottom())
  }

  // Enter sends the message; Shift+Enter inserts a newline. Skips IME
  // composition (so composing e.g. CJK text doesn't fire), and won't submit an
  // empty message with no attachment.
  submitOnEnter(event) {
    if (event.key !== "Enter" || event.shiftKey || event.isComposing) return
    event.preventDefault()
    if (!this.hasFormTarget) return

    const hasText = event.target.value.trim().length > 0
    const fileInput = this.formTarget.querySelector("input[type='file']")
    const hasFiles = fileInput && fileInput.files.length > 0
    if (hasText || hasFiles) this.formTarget.requestSubmit()
  }

  // --- backfill on wake (mobile socket suspend/reconnect) -------------------

  // Wire wake signals to refresh(): visibility (backgrounded PWA returns),
  // network back online, and window focus. Handlers are bound + stored on `this`
  // so disconnect() can remove them — Stimulus re-connects on every turbo-frame
  // load, so a leak would compound.
  setupWakeBackfill() {
    this.onVisibilityChange = () => {
      if (document.visibilityState === "visible") this.refresh()
    }
    this.onOnline = this.refresh.bind(this)
    this.onFocus = this.refresh.bind(this)

    document.addEventListener("visibilitychange", this.onVisibilityChange)
    window.addEventListener("online", this.onOnline)
    window.addEventListener("focus", this.onFocus)
  }

  teardownWakeBackfill() {
    if (this.onVisibilityChange) {
      document.removeEventListener("visibilitychange", this.onVisibilityChange)
    }
    if (this.onOnline) window.removeEventListener("online", this.onOnline)
    if (this.onFocus) window.removeEventListener("focus", this.onFocus)
  }

  // The conversation show path. Explicit data-thread-url-value wins (operator
  // inbox, where the thread lives in a turbo-frame); otherwise fall back to the
  // current path (portal/support render the thread as a full page, so the path
  // IS the show URL).
  showUrl() {
    return this.hasUrlValue && this.urlValue ? this.urlValue : window.location.pathname
  }

  // The inner messages container (#conversation_<id>_messages) — the node whose
  // innerHTML we swap. Its siblings (read marker, sentinel) are left untouched.
  messagesContainer() {
    return this.listTarget.querySelector("[id$='_messages']")
  }

  // The id of the newest (last) message bubble in a messages container, or null
  // when it has none. Used by refresh() to tell "nothing was missed" (same newest
  // bubble) from "there's something new to backfill".
  newestId(container) {
    const messages = container.querySelectorAll("[data-test='message']")
    const last = messages[messages.length - 1]
    return last ? last.id : null
  }

  // Re-fetch the thread and swap ONLY the messages container's innerHTML with the
  // freshly-rendered server markup. Because it's a full fresh render of that one
  // container (not an append), there's no duplication. The compose form lives
  // OUTSIDE the container, so in-progress typing is preserved. Read-receipt state
  // re-inits automatically: the fresh check/marker nodes fire the read-receipt
  // controller's *TargetConnected callbacks as they're inserted.
  async refresh() {
    if (this.refreshing) return
    if (!this.element.isConnected) return

    const container = this.messagesContainer()
    if (!container) return

    const url = this.showUrl()
    if (!url) return

    this.refreshing = true
    // Capture pin state BEFORE the swap (the swap changes scrollHeight).
    const wasPinned = this.pinnedToBottom

    try {
      const response = await fetch(url, {
        headers: { Accept: "text/html", "X-Requested-With": "XMLHttpRequest" },
      })
      if (!response.ok) throw new Error(`thread refresh ${response.status}`)
      const html = await response.text()
      const doc = new DOMParser().parseFromString(html, "text/html")

      const incoming = doc.querySelector(`#${CSS.escape(container.id)}`)
      if (!incoming) return

      const previousCount = container.querySelectorAll("[data-test='message']").length
      const incomingCount = incoming.querySelectorAll("[data-test='message']").length
      // Backfill only exists to recover messages MISSED while the socket slept.
      // A wake signal (focus/visibility/online) fires routinely — on initial page
      // load, on tab activation, on every window focus — even when the thread is
      // already current. If nothing was missed (same message count AND same newest
      // bubble), skip the swap entirely: churning the whole container's innerHTML
      // needlessly destroys + recreates every node, which both wastes work and, in
      // a driven browser, invalidates element references mid-interaction. This
      // makes refresh() a no-op in the common (nothing-missed) case.
      if (incomingCount <= previousCount && this.newestId(incoming) === this.newestId(container)) {
        return
      }

      // Pause the append observer across the wholesale swap so it doesn't
      // mis-read the fresh render as a live bottom-append (which would double the
      // pill count / auto-scroll). refresh() owns scroll + pill below. Flush any
      // records the swap queued before re-observing so a real broadcast that
      // lands mid-swap isn't dropped on the floor.
      if (this.observer) this.observer.disconnect()
      container.innerHTML = incoming.innerHTML
      const newCount = container.querySelectorAll("[data-test='message']").length
      if (this.observer) {
        this.observer.takeRecords()
        this.observer.observe(this.listTarget, { childList: true, subtree: true })
      }

      if (wasPinned) {
        // Reader was following the conversation → keep them at the newest message.
        requestAnimationFrame(() => this.scrollToBottom())
      } else if (newCount > previousCount) {
        // Reader was scrolled up and messages arrived below the fold → surface the
        // existing "New messages ↓" pill instead of yanking them down.
        this.newCount += newCount - previousCount
        this.showJump()
      }
    } catch (e) {
      // A failed refresh must not throw or disturb the live socket — log & move on.
      console.warn("thread backfill refresh failed", e)
    } finally {
      this.refreshing = false
    }
  }

  // --- reverse infinite scroll (older on scroll-up) -------------------------

  setupSentinel() {
    if (!this.hasSentinelTarget) return

    this.sentinelObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) this.loadOlder()
        })
      },
      { root: this.listTarget, threshold: 0.1 }
    )
    this.sentinelObserver.observe(this.sentinelTarget)
  }

  async loadOlder() {
    if (this.loadingOlder || !this.hasSentinelTarget) return
    const url = this.sentinelTarget.dataset.nextUrl
    if (!url) return

    this.loadingOlder = true
    const previousHeight = this.listTarget.scrollHeight

    try {
      const response = await fetch(url, { headers: { Accept: "text/html" } })
      if (!response.ok) throw new Error(`older page ${response.status}`)
      const html = await response.text()
      const doc = new DOMParser().parseFromString(html, "text/html")

      // The fetched page has its own messages container + (maybe) a newer
      // sentinel. Prepend the older messages ABOVE the current ones.
      const incomingList = doc.querySelector("[data-thread-target='list']")
      if (!incomingList) return

      const incomingMessages = incomingList.querySelectorAll("[data-test='message']")
      const anchor = this.messagesAnchor()
      incomingMessages.forEach((node) => anchor.parentNode.insertBefore(node, anchor))

      // Update or remove the sentinel with the next-older page.
      const incomingSentinel = doc.querySelector("[data-thread-target='sentinel']")
      if (incomingSentinel) {
        this.sentinelTarget.dataset.nextUrl = incomingSentinel.dataset.nextUrl
      } else {
        this.sentinelObserver.disconnect()
        this.sentinelTarget.remove()
      }

      // Keep the viewport steady: grow scrollTop by the height we just prepended.
      this.listTarget.scrollTop += this.listTarget.scrollHeight - previousHeight
    } catch (e) {
      // Swallow — a failed older-page fetch just leaves the thread as-is.
    } finally {
      this.loadingOlder = false
      // Replay any broadcast that landed mid-prepend so the jump pill is correct.
      this.reconcileAfterLoadOlder()
    }
  }

  // The messages container (older nodes are inserted before its first child).
  messagesAnchor() {
    const container = this.listTarget.querySelector("[id$='_messages']")
    return container ? container.firstElementChild || container : this.listTarget.firstElementChild
  }
}
